import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Divider, Icon, List, Grid, Card, Table, Message } from 'semantic-ui-react';
import './Playbook.css';

class SalesActivity extends Component {
    render() {
        return (
            <Container className="playbook-section-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Sales Activity
                </h1>
                <div className="ui divider"></div>

                <Segment className="playbook-content-segment">
                    <Header as="h2" color="orange">Plan Your Interactions</Header>
                    
                    <Header as="h3">Intentionality</Header>
                    <p>
                        When we talk about being intentional, we mean having a plan for every single encounter. That involves being very prepared and knowing:
                    </p>
                    <List numbered>
                        <List.Item><strong>What are we going to say?</strong></List.Item>
                        <List.Item><strong>What are we going to ask for?</strong></List.Item>
                        <List.Item><strong>What do we want the desired next step to be?</strong></List.Item>
                    </List>

                    <p>
                        You need to be able to answer these 3 questions when doing cold outreach, meeting a prospect for the 1st time, networking, 
                        or having a secondary meeting around a problem.
                    </p>

                    <Divider />

                    <Header as="h3" color="orange">Sales Activity – Explained</Header>
                    <p>
                        By now you have probably built out a message via the Multiply application, built a customer pitch deck, and are at various 
                        stages of training sales team members to carry the message effectively over the phone, in a casual setting, in a board room, 
                        or over a video conference. Now this is where we start talking about how we as sales leaders manage our teams to QUALITY of 
                        activity and not just QUANTITY of activity.
                    </p>

                    <Header as="h4">CRM Tools and Activity Tracking</Header>
                    <p>
                        We are often asked what flavor of CRM tool we prefer or recommend. Right now we don't have a preference. We don't have any 
                        partnerships, so we recommend that companies do their due diligence and pick the one they see their team most likely using.
                    </p>

                    <p>The intent of CRM tools is to manage a sales teams. These tools are very good at collecting sales activity and calculating metrics. Among other things, at its core, a CRM system will tell you:</p>
                    <List bulleted>
                        <List.Item>Who your sales team is talking to (contacts).</List.Item>
                        <List.Item>How many leads your team is following up on.</List.Item>
                        <List.Item>How many opportunities your team is managing and where they are in the funnel.</List.Item>
                        <List.Item>How large your pipeline is and where that is in conjunction with goals.</List.Item>
                    </List>

                    <Message warning>
                        <Message.Header>Critical Assumptions</Message.Header>
                        <p>However, the CRM tool does make assumptions as it relates to sales management. Those assumptions are that the sales team knows:</p>
                        <List bulleted>
                            <List.Item><strong>What to say.</strong></List.Item>
                            <List.Item><strong>When to say it.</strong></List.Item>
                            <List.Item><strong>How to say it.</strong></List.Item>
                        </List>
                        <p>Those are the assumptions we at Multiply fill in.</p>
                    </Message>

                    <Segment color="red">
                        <Header as="h4">The "Hire and Hope Strategy"</Header>
                        <p>If your approach is to:</p>
                        <List bulleted>
                            <List.Item>Stand up a CRM tool</List.Item>
                            <List.Item>Hire the "best" salespeople</List.Item>
                            <List.Item>Measure activity</List.Item>
                            <List.Item>Calculate results</List.Item>
                        </List>
                        <p>
                            Then your approach is what we call the "Hire and Hope Strategy". It can work but will generate VERY inconsistent results 
                            across a large sales team. This is why we have the 80/20 rule of sales (80% of sales comes from 20% of the team).
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="orange">Quality AND Quantity</Header>
                    <p>
                        So far, we have talked about the benefits of CRM tools and their ability to count and track activity and metrics, and we introduced 
                        the concept of managing to quality meetings rather than only quantity.
                    </p>

                    <p>When we say quality, we are talking about a meeting where we can define what we are asking for and know how to execute the conversation to get something from the prospect. It is NOT always about selling something. Different levels of "quality" include:</p>

                    <Grid stackable columns={3}>
                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>High Quality</Card.Header>
                                    <Card.Description>
                                        Meetings with prospects who fit inside of our desired prospect profiles and are potential buyers.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="yellow">
                                <Card.Content>
                                    <Card.Header>Medium Quality</Card.Header>
                                    <Card.Description>
                                        Meetings with Centers of Influence (COI) who can make introductions or create partnerships.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="blue">
                                <Card.Content>
                                    <Card.Header>Development Quality</Card.Header>
                                    <Card.Description>
                                        Meetings with prospects outside of our desired profiles with the intent of personal development or message testing.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Header as="h4">Key Questions for Meeting Quality</Header>
                    <List bulleted>
                        <List.Item><strong>Are we meeting with a potential buyer?</strong> The answer to this question is easily verified by determining if the prospect fits into one of the prospect profiles documented in the Multiply Application.</List.Item>
                        <List.Item><strong>If not, what are we trying to get out of the meeting?</strong> Personal development, business development, or center of influence cultivation.</List.Item>
                    </List>

                    <Divider />

                    <Header as="h3" color="orange">Time Management for Salespeople</Header>
                    <p>
                        Companies with mature sales teams can have various roles. However, MANY companies of all stages from concept to enterprise have 
                        a salesperson (or persons) who is responsible for the complete sales cycle, from creating the lead to closing.
                    </p>

                    <p>In this model, there are three (3) "buckets" where sales team members spend their time:</p>

                    <Grid stackable columns={3}>
                        <Grid.Column>
                            <Card color="red">
                                <Card.Content>
                                    <Card.Header>The Busy Bucket</Card.Header>
                                    <Card.Description>
                                        Administrative tasks, internal meetings, and non-revenue generating activities. This needs to be minimized.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="blue">
                                <Card.Content>
                                    <Card.Header>The Prospecting Bucket</Card.Header>
                                    <Card.Description>
                                        Cold outreach, networking, and creating new conversations with prospects you don't know.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card color="green">
                                <Card.Content>
                                    <Card.Header>The Account Management Bucket</Card.Header>
                                    <Card.Description>
                                        Protecting and growing current accounts, nurturing existing relationships.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Divider />

                    <Header as="h3" color="orange">Prospecting</Header>
                    <p>
                        Prospecting is the means of creating a new conversation with someone you don't know or with a current customer that you are 
                        looking to protect or grow.
                    </p>

                    <Header as="h4">Sample Cold Outreach</Header>
                    <p>
                        Let's start with a sample email (or LinkedIn DM) that can be used for you. There are all kinds of creative ways to do it. 
                        Cold outreach can take all kinds of forms from straight forward to catchy to creative. This is a straight forward approach 
                        that you can use and tailor to your company.
                    </p>

                    <Message warning>
                        <Message.Header>Disclaimer</Message.Header>
                        <p>
                            We don't recommend templates. we have an archive full of "what not to do" and email templates are very prevalent due to 
                            name mismatches, wrong company names, etc. These are simple to retype. Retype them. Use some variation and find your own 
                            voice that works for you and your company.
                        </p>
                    </Message>

                    <Segment>
                        <Header as="h5">Example Cold Email</Header>
                        <div style={{ fontFamily: 'monospace', background: '#f8f8f8', padding: '10px', borderRadius: '5px' }}>
                            Hi - My name is Ryan Robinett. I am part of the AskMultiply.com team. We have a unique approach to sales enablement by 
                            helping companies establish repeatable patterns of conversations that can be utilized in any prospect scenario.<br/><br/>
                            I would like the opportunity to tell you more about Multiply and learn more about and determine if there is a mutually 
                            beneficial partnership to be had.<br/><br/>
                            Can I get 45 minutes on your calendar in the next couple weeks?<br/><br/>
                            Thanks,<br/>
                            Ryan Robinett<br/>
                            ryan@askmultiply.com
                        </div>
                    </Segment>

                    <Header as="h4">By Any Means Necessary</Header>
                    <p>
                        Prospecting is a volume-based play. There is no wrong way to do it as long as you are:
                    </p>
                    <List bulleted>
                        <List.Item>Being intentional about your messaging and your ask</List.Item>
                        <List.Item>Testing messaging techniques to see what generates the most success</List.Item>
                        <List.Item>Using all the mediums</List.Item>
                    </List>

                    <p>What do we mean when we say "all of the mediums"? The four that we utilize the most and recommend our customers use are:</p>

                    <Grid stackable columns={4}>
                        <Grid.Column>
                            <Card>
                                <Card.Content textAlign="center">
                                    <Icon name="mail" size="large" color="blue" />
                                    <Card.Header>Email</Card.Header>
                                    <Card.Description>Direct and professional outreach</Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content textAlign="center">
                                    <Icon name="linkedin" size="large" color="blue" />
                                    <Card.Header>LinkedIn Messages</Card.Header>
                                    <Card.Description>Professional network connections</Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content textAlign="center">
                                    <Icon name="handshake" size="large" color="green" />
                                    <Card.Header>Networking Events</Card.Header>
                                    <Card.Description>Face-to-face relationship building</Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content textAlign="center">
                                    <Icon name="phone" size="large" color="orange" />
                                    <Card.Header>Phone Calls</Card.Header>
                                    <Card.Description>Direct conversation (follow-up)</Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Segment color="teal">
                        <Header as="h4">Multiply Tip</Header>
                        <p>
                            When you reach out to a prospect, do not route them to a link on a website or to a video of your product. Your purpose 
                            is to get that person to respond in some way. If you give a prospect the option to go click a link, the prospect may or 
                            may not click. And if they do click, they may watch while distracted, only watch half, not really watch, misunderstand 
                            the message, or watch it and love it but forget to respond—or any of the other infinite possibilities.
                        </p>
                        <p>
                            Be specific about your ask, and be simple. Ask for a meeting.
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="orange">Intentionality of Communication</Header>
                    <p>
                        When you reach out to someone, or when you have a meeting with someone, you should make sure you can answer three key questions:
                    </p>
                    <List numbered>
                        <List.Item>What am I going to say?</List.Item>
                        <List.Item>What am I going to ask for?</List.Item>
                        <List.Item>What is the desired next step?</List.Item>
                    </List>

                    <Message info>
                        <Message.Header>Remember these 2 rules for outbound sales:</Message.Header>
                        <List numbered>
                            <List.Item><strong>Nothing gets sold at the networking event!</strong> (Get a meeting)</List.Item>
                            <List.Item><strong>Nothing gets sold on email!</strong> (Get a meeting)</List.Item>
                        </List>
                        <p>
                            That is why we obsess over the first two encounters below, and they do different jobs. The Elevator Pitch earns you the first sit-down. The Casual Encounter is that sit-down, where your differentiators separate you and earn the problem-based meeting.
                        </p>
                    </Message>

                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Setting</Table.HeaderCell>
                                <Table.HeaderCell>What to Say</Table.HeaderCell>
                                <Table.HeaderCell>What to Ask For</Table.HeaderCell>
                                <Table.HeaderCell>Desired Next Step</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell><strong>Cold Outreach</strong><br/>email, LinkedIn, phone, drop by<br/><em>Practice as: Elevator Pitch</em></Table.Cell>
                                <Table.Cell>2-3 sentences on the story</Table.Cell>
                                <Table.Cell>A formal setting for a meeting (All forms – online, in person, casual, formal)</Table.Cell>
                                <Table.Cell>Commitment for an online or in-person meeting</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell><strong>Casual Encounter</strong><br/>networking event, lunch, drinks, coffee, social event<br/><em>Practice as: Casual Encounter</em></Table.Cell>
                                <Table.Cell>Short version of the story</Table.Cell>
                                <Table.Cell>A formal setting for a meeting (Online or in person)</Table.Cell>
                                <Table.Cell>Commitment for a meeting</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell><strong>Formal Meeting</strong><br/>In an office setting - online or in person<br/><em>Won with: your differentiators</em></Table.Cell>
                                <Table.Cell>The full version of the story</Table.Cell>
                                <Table.Cell>If the prospect has a problem that we solve</Table.Cell>
                                <Table.Cell>Identification of a problem – commitment to further discuss</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell><strong>Problem-Based Meeting</strong><br/>In an office setting - online or in person<br/><em>Where solutions are defined and closes begin</em></Table.Cell>
                                <Table.Cell>A brief version of the story for new people; a restatement of your purpose for a prospect that is not new</Table.Cell>
                                <Table.Cell>To create a proposal</Table.Cell>
                                <Table.Cell>Commitment to accept the proposal</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    <Divider />

                    <Header as="h3" color="orange">Selling to Current Customers</Header>
                    <p>
                        Many sales team members will be responsible for maintaining and building new clients. When looking at current accounts, the 
                        first goal is to ensure they don't go backwards. And the second goal is to grow those accounts.
                    </p>

                    <p>
                        When meeting with current customers, salespeople often find it "tricky" to figure out what to say. Your tactic is to create 
                        a meeting on the basis of providing the customer an update on your company. There usually are things that change with the 
                        overall story of your product or company on a fairly regular basis.
                    </p>

                    <List bulleted>
                        <List.Item>New clients that have been added</List.Item>
                        <List.Item>New service offerings that have been added or removed</List.Item>
                        <List.Item>New offices that your company has opened</List.Item>
                        <List.Item>Any other type of material change to the company</List.Item>
                    </List>

                    <Segment color="green">
                        <Header as="h4">Current Customer Meeting Strategy</Header>
                        <p>
                            If you create the conversation by giving your client an update on your company, then it gives you a good way to transition 
                            to the "ask", which is if there any updates or changes to the client's company. You also want to ask how the current 
                            execution of whatever it is your company provides is going.
                        </p>
                        <p>
                            This type of conversation not only maintains the business relationship by showing that you care about the current state 
                            of the relationship, but it also gives you an opportunity to create growth.
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="orange">Internal Preparation – Pre and Post Meeting</Header>
                    <p>
                        When running an internal sales meeting, we have all sorts of information to track as it relates to pipeline metrics. Those 
                        are great. We like those. But what we want is actionable information that results from very intentional questions.
                    </p>

                    <Header as="h4">Pre-Meeting Questions</Header>
                    <List bulleted>
                        <List.Item>Who are we meeting with?</List.Item>
                        <List.Item>What are we saying?</List.Item>
                        <List.Item>What are we asking for?</List.Item>
                        <List.Item>What is our desired next step?</List.Item>
                    </List>

                    <Header as="h4">Post-Meeting Questions</Header>
                    <List bulleted>
                        <List.Item>Did we say the things we intended to say?</List.Item>
                        <List.Item>Did we get a chance to ask as intended?</List.Item>
                        <List.Item>Did we get to a desired next step?</List.Item>
                        <List.Item>Did we identify the problem that we solve?</List.Item>
                        <List.Item>Are we able to submit a proposal?</List.Item>
                    </List>

                    <Message success>
                        <Message.Header>Key Principle</Message.Header>
                        <p>
                            We don't have "good" or "bad" meetings based on good conversations or a non-buyer. We have good meetings because we have 
                            a plan and we execute it well. Being very intentional about these questions makes following up on the "success" or 
                            "failure" of the meeting very simple.
                        </p>
                    </Message>

                    <p>
                        Ultimately, we want to get to a point where we have "permission" to write a proposal. The point is, we have meetings, and 
                        the goal is to be able to get to a point where we describe how we are going to work with the prospect. We obviously don't 
                        get to this step in every meeting. So we have to understand the prospect's next steps or anticipate the next meeting request 
                        with new stakeholders.
                    </p>

                    <Header as="h4">Opportunity Management Questions</Header>
                    <p>Assuming we know what our teams are supposed to say and we are being very intentional about preparation and getting to next steps, we should be asking ourselves:</p>
                    <List bulleted>
                        <List.Item>Have we told them the story?</List.Item>
                        <List.Item>Have we identified the problem that we solve?</List.Item>
                        <List.Item>Have we issued a proposal?</List.Item>
                    </List>

                    <p>
                        If our teams can't answer affirmatively to these steps, then there is more sales work to do, more people to tell the story to, 
                        and more next steps to set. Or it's time to disqualify the opportunity and move on.
                    </p>
                </Segment>
            </Container>
        );
    }
}

export default observer(SalesActivity);